import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, Search, Wrench, ShieldCheck, ChevronRight, Bug } from "lucide-react";

import bug1Problem from "@/assets/bug1-problem.png";
import bug1RootCause from "@/assets/bug1-rootcause.png";
import bug1Solution from "@/assets/bug1-solution.png";
import bug1Confidence from "@/assets/bug1-confidence.png";
import bug2Problem from "@/assets/bug2-problem.png";
import bug2RootCause from "@/assets/bug2-rootcause.png";
import bug2Solution from "@/assets/bug2-solution.png";
import bug2Confidence from "@/assets/bug2-confidence.png";

import bug1ProblemDark from "@/assets/bug1-problem-dark.png";
import bug1RootCauseDark from "@/assets/bug1-rootcause-dark.png";
import bug1SolutionDark from "@/assets/bug1-solution-dark.png";
import bug1ConfidenceDark from "@/assets/bug1-confidence-dark.png";
import bug2ProblemDark from "@/assets/bug2-problem-dark.png";
import bug2RootCauseDark from "@/assets/bug2-rootcause-dark.png";
import bug2SolutionDark from "@/assets/bug2-solution-dark.png";
import bug2ConfidenceDark from "@/assets/bug2-confidence-dark.png";

const stages = [
  { label: "Problem", icon: AlertTriangle, color: "text-destructive" },
  { label: "Root Cause", icon: Search, color: "text-amber-500" },
  { label: "Solution", icon: Wrench, color: "text-accent" },
  { label: "Fix Confidence", icon: ShieldCheck, color: "text-green-500" },
] as const;

const bugsLight = [
  {
    title: "Bug #1 — AbortError: signal is aborted without reason",
    images: [bug1Problem, bug1RootCause, bug1Solution, bug1Confidence],
  },
  {
    title: "Bug #2 — TypeError: Cannot read properties of undefined",
    images: [bug2Problem, bug2RootCause, bug2Solution, bug2Confidence],
  },
];

const bugsDark = [
  {
    title: "Bug #1 — AbortError: signal is aborted without reason",
    images: [bug1ProblemDark, bug1RootCauseDark, bug1SolutionDark, bug1ConfidenceDark],
  },
  {
    title: "Bug #2 — TypeError: Cannot read properties of undefined",
    images: [bug2ProblemDark, bug2RootCauseDark, bug2SolutionDark, bug2ConfidenceDark],
  },
];

const STAGE_DURATION = 2000;

export const BugShowcase = () => {
  const [bugIndex, setBugIndex] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains("dark"));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const bugs = isDark ? bugsDark : bugsLight;

  const advanceStage = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setStageIndex((prev) => {
        if (prev < 3) return prev + 1;
        // Move to next bug
        setBugIndex((b) => (b + 1) % bugs.length);
        return 0;
      });
      setIsTransitioning(false);
    }, 400);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(advanceStage, STAGE_DURATION);
    return () => clearInterval(timer);
  }, [advanceStage, isPaused]);

  const bug = bugs[bugIndex];
  const stage = stages[stageIndex];
  const StageIcon = stage.icon;

  const goToStage = (bIdx: number, sIdx: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setBugIndex(bIdx);
      setStageIndex(sIdx);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <div
      className="max-w-5xl mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Bug selector tabs */}
      <div className="flex justify-center gap-3 mb-8">
        {bugs.map((b, bIdx) => (
          <button
            key={bIdx}
            onClick={() => goToStage(bIdx, 0)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300 text-sm font-medium ${
              bIdx === bugIndex
                ? "border-accent bg-accent/10 text-accent"
                : "border-border bg-card text-muted-foreground hover:border-accent/50"
            }`}
          >
            <Bug className="w-4 h-4" />
            Bug #{bIdx + 1}
          </button>
        ))}
      </div>

      {/* Stage progress pipeline */}
      <div className="flex items-center justify-center gap-1 sm:gap-2 mb-8">
        {stages.map((s, sIdx) => {
          const Icon = s.icon;
          const isActive = sIdx === stageIndex;
          const isDone = sIdx < stageIndex;
          return (
            <div key={sIdx} className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => goToStage(bugIndex, sIdx)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all duration-300 text-xs sm:text-sm font-medium ${
                  isActive
                    ? `border-current ${s.color} bg-current/10`
                    : isDone
                    ? "border-accent/30 text-accent/60 bg-accent/5"
                    : "border-border text-muted-foreground hover:border-muted-foreground/50"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isActive ? s.color : ""}`} />
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {sIdx < 3 && (
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40" />
              )}
            </div>
          );
        })}
      </div>

      {/* Current bug title */}
      <div className="text-center mb-6">
        <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground">
          {bug.title}
        </h3>
        <div className={`inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full text-sm font-medium ${stage.color}`}>
          <StageIcon className="w-4 h-4" />
          Healion identified the {stage.label}
        </div>
      </div>

      {/* Image display with animation */}
      <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-lg">
        <div
          className={`transition-all duration-400 ${
            isTransitioning
              ? "opacity-0 translate-y-4 scale-[0.98]"
              : "opacity-100 translate-y-0 scale-100"
          }`}
        >
          <img
            src={bug.images[stageIndex]}
            alt={`${bug.title} - ${stage.label}`}
            className="w-full h-auto"
            loading="lazy"
          />
        </div>

        {/* Stage indicator overlay */}
        <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-sm border border-border text-xs font-medium">
          <span className={`w-2 h-2 rounded-full ${
            stageIndex === 0 ? "bg-destructive" :
            stageIndex === 1 ? "bg-amber-500" :
            stageIndex === 2 ? "bg-accent" : "bg-green-500"
          } animate-pulse`} />
          Step {stageIndex + 1} of 4
        </div>
      </div>

      {/* Auto-play progress bar */}
      <div className="mt-4 flex gap-1.5">
        {stages.map((_, sIdx) => (
          <div key={sIdx} className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                sIdx < stageIndex
                  ? "w-full bg-accent"
                  : sIdx === stageIndex && !isPaused
                  ? "bg-accent animate-[progress_2s_linear]"
                  : sIdx === stageIndex && isPaused
                  ? "w-1/2 bg-accent"
                  : "w-0"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
